import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { presetAPI, attractionsAPI } from '../services/api';
import './ItineraryPresetDetail.css';

const SEGMENTS = ['Morning', 'Afternoon', 'Evening'];

const ItineraryPresetDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [preset, setPreset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editMeta, setEditMeta] = useState({ title: '', hotel: '', theme: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [addDay, setAddDay] = useState(1);
  const [addSegment, setAddSegment] = useState('Morning');
  const [addSequence, setAddSequence] = useState(1);
  const [addSearchQuery, setAddSearchQuery] = useState('');
  const [addSearchResults, setAddSearchResults] = useState([]);
  const [addSearching, setAddSearching] = useState(false);
  const [selectedAttraction, setSelectedAttraction] = useState(null);

  const clearMsg = () => { setTimeout(() => { setError(''); setSuccess(''); }, 3000); };

  const loadPreset = useCallback(async () => {
    try {
      setLoading(true);
      const data = await presetAPI.getById(id);
      const p = data.data || data;
      setPreset(p);
      setEditMeta({ title: p.title || '', hotel: p.hotel_name || '', theme: p.theme || '' });
    } catch {
      setError('Failed to load preset.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadPreset(); }, [loadPreset]);

  const handleSaveMeta = async () => {
    try {
      setSaving(true);
      await presetAPI.update(id, { title: editMeta.title, hotel_name: editMeta.hotel, theme: editMeta.theme });
      setSuccess('Preset updated.');
      clearMsg();
      loadPreset();
    } catch {
      setError('Failed to update preset.');
      clearMsg();
    } finally {
      setSaving(false);
    }
  };

  const handleAttractionSearch = async (query) => {
    setAddSearchQuery(query);
    if (!query.trim() || query.trim().length < 2) { setAddSearchResults([]); return; }
    try {
      setAddSearching(true);
      const data = await attractionsAPI.getAll({ search: query, limit: 10 });
      setAddSearchResults(data.data || data.attractions || []);
    } catch {
      setAddSearchResults([]);
    } finally {
      setAddSearching(false);
    }
  };

  const clearAddForm = () => {
    setSelectedAttraction(null); setAddSearchQuery(''); setAddSearchResults([]);
  };

  const handleAddAttraction = async (e) => {
    e.preventDefault();
    if (!selectedAttraction) { setError('Select an attraction from the search results.'); clearMsg(); return; }
    try {
      await presetAPI.addAttraction(id, {
        attraction_id: selectedAttraction.id,
        day_number: addDay,
        segment: addSegment,
        sequence_order: addSequence,
      });
      setSuccess('Attraction added.');
      clearAddForm(); setShowAddForm(false);
      clearMsg();
      loadPreset();
    } catch {
      setError('Failed to add attraction.');
      clearMsg();
    }
  };

  const handleRemove = async (attractionId) => {
    if (!window.confirm('Remove this attraction?')) return;
    try {
      await presetAPI.removeAttraction(id, attractionId);
      setSuccess('Attraction removed.');
      clearMsg();
      loadPreset();
    } catch {
      setError('Failed to remove attraction.');
      clearMsg();
    }
  };

  const handleFix = async (attractionId) => {
    try {
      await presetAPI.fixAttraction(id, attractionId);
      setSuccess('Attraction fix attempted.');
      clearMsg();
      loadPreset();
    } catch {
      setError('Failed to fix attraction.');
      clearMsg();
    }
  };

  if (loading) return <Layout title="Preset Detail"><div className="itin-loading">Loading...</div></Layout>;
  if (!preset) return <Layout title="Preset Detail"><div className="itin-error">{error || 'Preset not found.'}</div></Layout>;

  // API returns days as an object keyed by day number: { "1": { Morning: [...], Afternoon: [...] } }
  const daysObj = preset.days || {};
  const dayKeys = Object.keys(daysObj).sort((a, b) => Number(a) - Number(b));

  return (
    <Layout title="Preset Detail">
      <div className="itin-preset-detail">
        <button className="itin-back-btn" onClick={() => navigate('/itinerary/presets')}>← Back to Presets</button>

        {error   && <div className="itin-alert error">{error}</div>}
        {success && <div className="itin-alert success">{success}</div>}

        {/* Metadata editor */}
        <div className="itin-card">
          <h3 className="itin-card-title">Preset Metadata</h3>
          <div className="itin-meta-form">
            <div className="itin-field">
              <label>Title</label>
              <input
                value={editMeta.title}
                onChange={e => setEditMeta(m => ({ ...m, title: e.target.value }))}
                className="itin-input"
              />
            </div>
            <div className="itin-field">
              <label>Hotel</label>
              <input
                value={editMeta.hotel}
                onChange={e => setEditMeta(m => ({ ...m, hotel: e.target.value }))}
                className="itin-input"
              />
            </div>
            <div className="itin-field">
              <label>Theme</label>
              <input
                value={editMeta.theme}
                onChange={e => setEditMeta(m => ({ ...m, theme: e.target.value }))}
                className="itin-input"
              />
            </div>
            <button className="itin-save-btn" onClick={handleSaveMeta} disabled={saving}>
              {saving ? 'Saving…' : '💾 Save Changes'}
            </button>
          </div>
        </div>

        {/* Attractions by day/slot */}
        <div className="itin-card">
          <div className="itin-card-title-row">
            <h3 className="itin-card-title">Attractions</h3>
            <button className="itin-add-btn" onClick={() => setShowAddForm(f => !f)}>
              {showAddForm ? '✕ Cancel' : '+ Add Attraction'}
            </button>
          </div>

          {showAddForm && (
            <form className="itin-add-form itin-add-search-form" onSubmit={handleAddAttraction}>
              <div className="itin-search-row">
                <input
                  className="itin-input"
                  placeholder="Search attraction..."
                  value={addSearchQuery}
                  onChange={e => handleAttractionSearch(e.target.value)}
                  autoFocus
                />
                {addSearching && <span className="itin-searching">Searching…</span>}
              </div>
              {addSearchResults.length > 0 && !selectedAttraction && (
                <div className="itin-search-results">
                  {addSearchResults.map(r => (
                    <button key={r.id} type="button" className="itin-search-result-item"
                      onClick={() => setSelectedAttraction(r)}>
                      <span className="itin-sr-name">{r.name}</span>
                      <span className="itin-sr-cat">{r.category}</span>
                    </button>
                  ))}
                </div>
              )}
              {selectedAttraction && (
                <div className="itin-selected-attr">
                  <span>✓ {selectedAttraction.name}</span>
                  <button type="button" className="itin-clear-sel" onClick={clearAddForm}>✕</button>
                </div>
              )}
              <div className="itin-add-slots">
                <select className="itin-select" value={addDay} onChange={e => setAddDay(Number(e.target.value))}>
                  {[1, 2, 3].map(d => <option key={d} value={d}>Day {d}</option>)}
                </select>
                <select className="itin-select" value={addSegment} onChange={e => setAddSegment(e.target.value)}>
                  {SEGMENTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <input type="number" min={1} max={10} className="itin-input itin-seq-input"
                  value={addSequence} onChange={e => setAddSequence(Number(e.target.value))}
                  placeholder="Seq #" />
                <button type="submit" className="itin-save-btn" disabled={!selectedAttraction}>Add</button>
              </div>
            </form>
          )}

          {dayKeys.length === 0 ? (
            <div className="itin-empty">No attractions configured yet.</div>
          ) : dayKeys.map(day => (
            <div key={day} className="itin-day-block">
              <div className="itin-day-label">Day {day}</div>
              {SEGMENTS.map(segment => {
                const rows = (daysObj[day] || {})[segment] || [];
                if (rows.length === 0) return null;
                return (
                  <div key={segment} className="itin-slot-block">
                    <div className="itin-slot-sublabel">
                      {segment === 'Morning' ? '🌅' : segment === 'Afternoon' ? '☀️' : '🌙'} {segment}
                    </div>
                    {rows.map((a, i) => (
                      <div key={a.id || i} className={`itin-attr-row ${a.is_matched === false ? 'unmatched' : ''}`}>
                        <span className="itin-seq-badge">{a.sequence_order}</span>
                        <div className="itin-attr-info">
                          <span className="itin-attr-name">{a.details?.name || a.place_name}</span>
                          {a.details?.name && a.details.name !== a.place_name && (
                            <span className="itin-attr-orig">({a.place_name})</span>
                          )}
                          {a.details?.category && <span className="itin-attr-cat">{a.details.category}</span>}
                          {a.match_method && (
                            <span className={`itin-match-badge ${a.match_method}`}>{a.match_method}</span>
                          )}
                          {a.is_matched === false && (
                            <span className="itin-unmatched-badge">⚠️ Unmatched</span>
                          )}
                        </div>
                        {a.details?.first_image && (
                          <img src={a.details.first_image} alt="" className="itin-attr-thumb"
                            onError={e => { e.target.style.display = 'none'; }} />
                        )}
                        <div className="itin-attr-actions">
                          {a.is_matched === false && (
                            <button className="itin-btn-icon fix" title="Fix unmatched"
                              onClick={() => handleFix(a.id)}>🔧</button>
                          )}
                          <button className="itin-btn-icon remove" title="Remove"
                            onClick={() => handleRemove(a.id)}>🗑️</button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default ItineraryPresetDetail;
